import { memo, ReactNode } from "react";

import { FormControl } from "@mui/material";
import Box from "@mui/material/Box";
import FormLabel from "@mui/joy/FormLabel";
import { Textarea } from "@mui/joy";

/**
 * Props for Text Area Component.
 * @interface
 * @name TextAreaProps
 * @kind interface
 */
interface TextAreaProps {
  /**
   * Id for the label for the Text Area Component.
   * @property
   * @name labelId
   * @kind property
   * @memberof SelectProps
   * @type {string}
   */
  labelId: string;
  /**
   * (optional)
   * Standard HTML Attribute, for label classes.
   * @property
   * @name labelClassName
   * @kind property
   * @memberof SelectProps
   * @type {string | undefined}
   */
  labelClassName?: string;
  /**
   * Label for the Text Area Component.
   * @property
   * @name label
   * @kind property
   * @memberof TextAreaProps
   * @type {ReactNode}
   */
  label: ReactNode;
  /**
   * Id for the Text Area Component.
   * @property
   * @name id
   * @kind property
   * @memberof TextAreaProps
   * @type {string}
   */
  id: string;
  /**
   * Standard HTML Attribute, input value.
   * @property
   * @name value
   * @kind property
   * @memberof TextAreaProps
   * @type {string}
   */
  value: string;
  /**
   * Standard HTML Attribute, input placeholder.
   * @property
   * @name placeholder
   * @kind property
   * @memberof TextAreaProps
   * @type {string}
   */
  placeholder: string;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof TextAreaProps
   * @type {string | undefined}
   */
  className?: string;
  /**
   * (optional)
   * Standard HTML Attribute, for container classes.
   * @property
   * @name containerClassName
   * @kind property
   * @memberof TextAreaProps
   * @type {string | undefined}
   */
  containerClassName?: string;
  /**
   * (optional)
   * Standard HTML Attribute.
   * If `true`, disables the input.
   * @property
   * @name disabled
   * @kind property
   * @memberof TextAreaProps
   * @type {boolean | undefined}
   */
  disabled?: boolean;
  /**
   * (optional)
   * Maximum number of rows to display.
   * @property
   * @name maxRows
   * @kind property
   * @memberof TextAreaProps
   * @type {string | number | undefined}
   */
  maxRows?: string | number;
  /**
   * (optional)
   * Minimum number of rows to display.
   * @default 1
   * @property
   * @name minRows
   * @kind property
   * @memberof TextAreaProps
   * @type {string | number | undefined}
   */
  minRows?: string | number;
  /**
   * (optional)
   * Standard HTML Attribute, maximum length number.
   * @property
   * @name maxLength
   * @kind property
   * @memberof TextAreaProps
   * @type {number | undefined}
   */
  maxLength?: number;
  /**
   * (optional)
   * If `true`, the error message is shown.
   * @property
   * @name hasError
   * @kind property
   * @memberof TextAreaProps
   * @type {boolean | undefined}
   */
  hasError?: boolean;
  /**
   * Callback fired when the value changes.
   * @property
   * @name onChange
   * @kind property
   * @memberof TextAreaProps
   * @type {React.ChangeEventHandler<HTMLTextAreaElement>}
   */
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
}

/**
 * Memoized Text Area Component.
 * @constant
 * @name TextAreaComponent
 * @kind variable
 * @type {React.NamedExoticComponent<TextAreaProps>}
 */
const TextAreaComponent: React.NamedExoticComponent<TextAreaProps> = memo(
  /**
   * Text Area Component.
   * @function
   * @name TextAreaComponent
   * @kind function
   * @memberof TextAreaComponent
   * @param {TextAreaProps} props Props for Text Area Component.
   * @returns {JSX.Element} Text Area Component.
   */
  function TextAreaComponent(props: TextAreaProps): JSX.Element {
    return (
      <Box className={props.containerClassName}>
        <FormControl fullWidth>
          <FormLabel
            className={`label ${props.labelClassName}`}
            id={props.labelId}
            htmlFor={props.id}
          >
            {props.label}
          </FormLabel>
          <Textarea
            id={props.id}
            className={`text-area ${props.className}  ${
              props.hasError ? "border-red-500" : ""
            } ${
              props.disabled ? "bg-[#F9F9F9]" : "bg-white"
            } [&:has(textarea:disabled)]:bg-[#F9F9F9]`}
            minRows={props.minRows}
            maxRows={props.maxRows}
            placeholder={props.placeholder}
            disabled={props.disabled}
            value={props.value}
            onChange={props.onChange}
            slotProps={{
              textarea: {
                id: props.id,
                className:
                  "focus:outline-none focus:border-transparent focus:ring-0",
                maxLength: props.maxLength,
              },
            }}
            sx={{
              // changes the placeholder value color
              "textarea::placeholder": {
                color: "#C6C6C6",
              },
              "&.Mui-focused": {
                "--Textarea-focusedHighlight": "#1A1C1F",
              },
            }}
          />
        </FormControl>
      </Box>
    );
  }
);

export default TextAreaComponent;
